import axios from 'axios';

const rawApiBase = (process.env.REACT_APP_API_BASE || '127.0.0.1:8001').trim();
const hasProtocol = /^https?:\/\//i.test(rawApiBase);
const defaultProtocol = process.env.NODE_ENV === 'development' ? 'http' : 'https';
export const API_BASE = hasProtocol ? rawApiBase : `${defaultProtocol}://${rawApiBase}`;

// Create axios instance with auth interceptor
const api = axios.create({
  baseURL: API_BASE,
});

// Add auth token to all requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('access');
      window.location.href = '/';
    }
    return Promise.reject(error);
  }
);

// ============ VEHICLES ============
export const vehiclesAPI = {
  getAll: () => api.get('/api/vehicles/'),
  getAllLocations: () => api.get('/api/all-current-locations/'),
  getLocation: (vehicleId) => api.get(`/api/vehicle-location/${vehicleId}/`),
  getLocationHistory: (vehicleId, date) => api.get(`/api/vehicle-locations/${vehicleId}/${date}/`),
};

// ============ ROUTES ============
/**
 * Routes API
 * 
 * Route Types: 'delivery' | 'sales' | 'collection' | 'mixed'
 * 
 * Create Route Request:
 * {
 *   name: string (required),
 *   code: string (required, e.g., 'R001', 'NORTH-1'),
 *   description?: string,
 *   depot_id: uuid (required),
 *   route_type?: 'delivery' | 'sales' | 'collection' | 'mixed',
 *   corridor_meters?: number (buffer distance),
 *   active_days?: number[] (0=Monday, 6=Sunday),
 *   color_hex?: string (e.g., '#FF5733'),
 *   notify_salespoints?: boolean,
 *   notification_distance_meters?: number,
 *   salespoint_ids?: uuid[] (list of salespoint IDs to add as waypoints),
 *   optimize_order?: boolean (default: true)
 * }
 * 
 * Add Waypoint Request:
 * {
 *   salespoint_id: uuid (required),
 *   sequence_order?: number (default: 1),
 *   is_required?: boolean,
 *   is_active?: boolean,
 *   expected_stop_duration_minutes?: number,
 *   notify_on_approach?: boolean,
 *   approach_notification_meters?: number
 * }
 * 
 * Reorder Waypoints Request:
 * {
 *   waypoint_orders: [{ waypoint_id: uuid, sequence_order: number }, ...]
 * }
 * 
 * Remove Waypoint Request:
 * {
 *   waypoint_id: uuid
 * }
 */
export const routesAPI = {
  // List & CRUD
  getAll: () => api.get('/api/routes/'),
  get: (id) => api.get(`/api/routes/${id}/`),
  create: (data) => api.post('/api/routes/', data),
  update: (id, data) => api.put(`/api/routes/${id}/`, data),
  patch: (id, data) => api.patch(`/api/routes/${id}/`, data),
  delete: (id) => api.delete(`/api/routes/${id}/`),
  
  // GeoJSON
  getGeoJSON: (id) => api.get(`/api/routes/${id}/geojson/`),
  getAllGeoJSON: () => api.get('/api/routes/geojson_all/'),
  
  // Waypoints
  addWaypoint: (id, data) => api.post(`/api/routes/${id}/add_waypoint/`, data),
  addWaypointsBulk: (id, data) => api.post(`/api/routes/${id}/add_waypoints_bulk/`, data),
  removeWaypoint: (id, waypointId, deleteSalespoint = false) => api.post(`/api/routes/${id}/remove_waypoint/`, { waypoint_id: waypointId, delete_salespoint: deleteSalespoint }),
  reorderWaypoints: (id, waypointIds) => api.post(`/api/routes/${id}/reorder_waypoints/`, { waypoint_ids: waypointIds }),
  
  // Route operations
  optimize: (id, data = {}) => api.post(`/api/routes/${id}/optimize/`, data),
  getCurrentVehicles: (id) => api.get(`/api/routes/${id}/current_vehicles/`),
  getSessions: (id) => api.get(`/api/routes/${id}/sessions/`),
  
  // Detection & Generation
  detect: (data) => api.post('/api/routes/detect/', data),
  autoGenerate: (data) => api.post('/api/routes/auto-generate/', data),
  
  // By vehicle/depot
  getByVehicle: (vehicleId) => api.get(`/api/routes/vehicle/${vehicleId}/`),
  getByDepot: (depotId) => api.get(`/api/routes/depot/${depotId}/`),
};

// ============ SUB-ROUTES (BRANCHES) ============
export const subRoutesAPI = {
  list: (routeId) => api.get('/api/routes/subroutes/', { params: { route_id: routeId } }),
  get: (id) => api.get(`/api/routes/subroutes/${id}/`),
  create: (data) => api.post('/api/routes/subroutes/', data),
  update: (id, data) => api.put(`/api/routes/subroutes/${id}/`, data),
  patch: (id, data) => api.patch(`/api/routes/subroutes/${id}/`, data),
  delete: (id) => api.delete(`/api/routes/subroutes/${id}/`),
  addWaypoint: (id, data) => api.post(`/api/routes/subroutes/${id}/add_waypoint/`, data),
  addWaypointsBulk: (id, data) => api.post(`/api/routes/subroutes/${id}/add_waypoints_bulk/`, data),
  removeWaypoint: (id, waypointId) => api.post(`/api/routes/subroutes/${id}/remove_waypoint/`, { waypoint_id: waypointId }),
  reorderWaypoints: (id, waypointIds) => api.post(`/api/routes/subroutes/${id}/reorder_waypoints/`, { waypoint_ids: waypointIds }),
  optimize: (id) => api.post(`/api/routes/subroutes/${id}/optimize/`),
};

// ============ EXIT CORRIDORS ============
export const exitCorridorsAPI = {
  list: (depotId) => api.get('/api/routes/exit-corridors/', { params: depotId ? { depot_id: depotId } : {} }),
  get: (id) => api.get(`/api/routes/exit-corridors/${id}/`),
  create: (data) => api.post('/api/routes/exit-corridors/', data),
  update: (id, data) => api.put(`/api/routes/exit-corridors/${id}/`, data),
  patch: (id, data) => api.patch(`/api/routes/exit-corridors/${id}/`, data),
  delete: (id) => api.delete(`/api/routes/exit-corridors/${id}/`),
  addWaypoint: (id, data) => api.post(`/api/routes/exit-corridors/${id}/add_waypoint/`, data),
  addWaypointsBulk: (id, data) => api.post(`/api/routes/exit-corridors/${id}/add_waypoints_bulk/`, data),
  removeWaypoint: (id, waypointId) => api.post(`/api/routes/exit-corridors/${id}/remove_waypoint/`, { waypoint_id: waypointId }),
  reorderWaypoints: (id, waypointIds) => api.post(`/api/routes/exit-corridors/${id}/reorder_waypoints/`, { waypoint_ids: waypointIds }),
};

// ============ ROUTE ENTRY POINTS ============
export const routeEntryPointsAPI = {
  list: (routeId) => api.get('/api/routes/entry-points/', { params: routeId ? { route_id: routeId } : {} }),
  get: (id) => api.get(`/api/routes/entry-points/${id}/`),
  create: (routeId, data) => api.post('/api/routes/entry-points/', { route_id: routeId, ...data }),
  update: (id, data) => api.put(`/api/routes/entry-points/${id}/`, data),
  patch: (id, data) => api.patch(`/api/routes/entry-points/${id}/`, data),
  delete: (id) => api.delete(`/api/routes/entry-points/${id}/`),
};

// ============ ROUTE ASSIGNMENTS ============
/**
 * Route Assignments API
 *
 * Create Assignment Request:
 * {
 *   vehicle_id: uuid (required),
 *   route_id: uuid (required),
 *   is_primary?: boolean,
 *   is_active?: boolean,
 *   active_days?: number[] (0=Monday, 6=Sunday)
 * }
 */
export const routeAssignmentsAPI = {
  getAll: () => api.get('/api/routes/assignments/'),
  get: (id) => api.get(`/api/routes/assignments/${id}/`),
  create: (data) => api.post('/api/routes/assignments/', data),
  update: (id, data) => api.put(`/api/routes/assignments/${id}/`, data),
  patch: (id, data) => api.patch(`/api/routes/assignments/${id}/`, data),
  delete: (id) => api.delete(`/api/routes/assignments/${id}/`),
};

// ============ ROUTE SESSIONS ============
/**
 * Route Sessions API
 * 
 * Session includes:
 * - vehicle, route info
 * - started_at, ended_at, is_active
 * - current_waypoint_index
 * - detection_method: 'assigned' | 'detected' | 'manual'
 * - detection_confidence: 0-1
 * - distance_traveled_km, stops_made
 * - duration_minutes, progress_percentage
 * - waypoint_visits[]
 */
export const routeSessionsAPI = {
  getAll: () => api.get('/api/routes/sessions/'),
  get: (id) => api.get(`/api/routes/sessions/${id}/`),
  endSession: (id) => api.post(`/api/routes/sessions/${id}/end_session/`),
};

// ============ ROUTE STATES ============
/**
 * Route States API - Current state of vehicles on routes
 * 
 * State includes:
 * - vehicle info
 * - is_on_route
 * - current_route, next_waypoint
 * - distance_to_next_waypoint_meters
 * - eta_to_next_waypoint_minutes
 * - routes_today, waypoints_visited_today
 */
export const routeStatesAPI = {
  getAll: () => api.get('/api/routes/states/'),
  get: (id) => api.get(`/api/routes/states/${id}/`),
};

// ============ ROUTE NOTIFICATIONS ============
/**
 * Route Notifications API - Salespoint approach notifications
 * 
 * Notification includes:
 * - session, waypoint info
 * - notification_type
 * - distance_meters, estimated_arrival_minutes
 * - status: 'pending' | 'sent' | 'failed' | 'acknowledged'
 * - sent_at, acknowledged_at
 * - whatsapp_sent, sms_sent
 */
export const routeNotificationsAPI = {
  getAll: () => api.get('/api/routes/notifications/'),
  get: (id) => api.get(`/api/routes/notifications/${id}/`),
  acknowledge: (id) => api.post(`/api/routes/notifications/${id}/acknowledge/`),
};

// ============ USERS ============
export const usersAPI = {
  getAll: () => api.get('/api/auth/users/'),
  getProfile: () => api.get('/api/auth/profile/'),
};

// ============ SALES POINTS ============
export const salesPointsAPI = {
  getAll: (params) => api.get('/api/sales-points/', { params }),
  get: (id) => api.get(`/api/sales-points/${id}/`),
};

// ============ DEPOTS ============
// Depots are stored as organizations in the backend
export const depotsAPI = {
  getAll: () => api.get('/api/organizations/'),
  get: (id) => api.get(`/api/organizations/${id}/`),
};

// ============ ANALYTICS ============
export const analyticsAPI = {
  getClusters: () => api.get('/api/analytics/cluster-analytics/'),
  getDashboard: () => api.get('/api/dashboard-summary/'),
  getStopPoints: () => api.get('/api/analytics/stop-points/'),
};

// ============ GEOFENCING ============
export const geofencingAPI = {
  getAll: () => api.get('/api/geofencing/geofences/'),
  get: (id) => api.get(`/api/geofencing/geofences/${id}/`),
  create: (data) => api.post('/api/geofencing/geofences/', data),
  update: (id, data) => api.put(`/api/geofencing/geofences/${id}/`, data),
  delete: (id) => api.delete(`/api/geofencing/geofences/${id}/`),
};

// ============ TRIPS ============
export const tripsAPI = {
  getAll: () => api.get('/api/trips/'),
  get: (id) => api.get(`/api/trips/${id}/`),
  getStats: () => api.get('/api/trips/stats/'),
};

export default api;
